import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';

// Google Identity Services, als externes Skript geladen
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize(o: { client_id: string; callback: (r: { credential: string }) => void; ux_mode?: string }): void;
          renderButton(el: HTMLElement, o: Record<string, unknown>): void;
        };
      };
    };
  }
}

const GIS_SRC = 'https://accounts.google.com/gsi/client';

function loadGis(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve();
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SRC}"]`);
    const script = existing ?? Object.assign(document.createElement('script'), { src: GIS_SRC, async: true, defer: true });
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () => reject(new Error('Google-Skript nicht geladen')));
    if (!existing) document.head.appendChild(script);
  });
}

/** Button „Mit Google anmelden“. Erscheint nur, wenn der Server eine Client-ID kennt. */
export function GoogleButton({ mode, teamName, onToken, onError }: {
  mode: 'login' | 'register';
  teamName: string;
  onToken: () => void;
  onError: (msg: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [clientId, setClientId] = useState<string | null>(null);
  const teamRef = useRef(teamName);
  teamRef.current = teamName;

  useEffect(() => {
    api<{ googleClientId: string | null }>('/auth/config')
      .then((r) => setClientId(r.googleClientId))
      .catch(() => setClientId(null));
  }, []);

  useEffect(() => {
    if (!clientId || !ref.current) return;
    let cancelled = false;
    loadGis()
      .then(() => {
        if (cancelled || !ref.current || !window.google) return;
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: async ({ credential }) => {
            try {
              const r = await api<{ token: string }>('/auth/google', { method: 'POST', json: { credential, teamName: teamRef.current } });
              onToken();
            } catch (err) {
              onError(err instanceof Error ? err.message : 'Google-Anmeldung fehlgeschlagen');
            }
          },
        });
        window.google.accounts.id.renderButton(ref.current, {
          type: 'standard',
          theme: document.documentElement.dataset.theme === 'dark' ? 'filled_black' : 'outline',
          size: 'large',
          shape: 'pill',
          width: Math.min(ref.current.offsetWidth || 320, 400),
          text: mode === 'login' ? 'signin_with' : 'signup_with',
          logo_alignment: 'center',
          locale: 'de',
        });
      })
      .catch(() => setClientId(null));
    return () => {
      cancelled = true;
    };
  }, [clientId, mode, onToken, onError]);

  if (!clientId) return null;
  return (
    <>
      <div className="flex items-center gap-3 text-[13px] text-[var(--dim)]">
        <span className="h-px flex-1 bg-[var(--line)]" />oder<span className="h-px flex-1 bg-[var(--line)]" />
      </div>
      <div ref={ref} className="flex min-h-[44px] justify-center" />
    </>
  );
}
